import React, { useState, useEffect, useCallback, useMemo } from "react";
import Map, { NavigationControl, Source, Layer, Marker, Popup } from "react-map-gl";
import axios from "axios";
import 'mapbox-gl/dist/mapbox-gl.css';
import { Box, Typography, IconButton } from '@mui/material';
import { Info, X } from 'lucide-react';


const MAPBOX_TOKEN = "pk.eyJ1Ijoiam9ub2hvd2VsbCIsImEiOiJjbHR6NW1jZjMwbWYyMmlsYTd1dnJvZTlnIn0.g3MBcCegx1QPIfOjmFq6nQ";
// Eclipse Tint coordinates for route calculation
const ECLIPSE_TINT_COORDS = { lat: -38.0087, lng: 145.1077 };

const SiteMap = ({ siteLat, siteLng, siteName }) => {
    const [distance, setDistance] = useState(null);
    const [duration, setDuration] = useState(null);
    const [loading, setLoading] = useState(true);
    const [routeGeoJson, setRouteGeoJson] = useState(null);
    const [showPopup, setShowPopup] = useState(false);
    const [popupInfo, setPopupInfo] = useState(null);
    const [showRouteInfo, setShowRouteInfo] = useState(false);

    const [viewState, setViewState] = useState({
        latitude: (ECLIPSE_TINT_COORDS.lat + siteLat) / 2,
        longitude: (ECLIPSE_TINT_COORDS.lng + siteLng) / 2,
        zoom: 11,
        bearing: 0,
        pitch: 0,
    });

    // Route line style
    const routeLineLayer = {
        id: 'route',
        type: 'line',
        layout: {
            'line-join': 'round',
            'line-cap': 'round'
        },
        paint: {
            'line-color': '#3b82f6',
            'line-width': 5,
            'line-opacity': 0.8
        }
    };

    // Handle clicking on map markers
    const handleMarkerClick = useCallback((info, type) => {
        setPopupInfo({
            longitude: info.longitude,
            latitude: info.latitude,
            title: type === 'start' ? 'Eclipse Tint Office' : siteName,
            type
        });
        setShowPopup(true);
    }, [siteName]);

    // Fetch the route when coordinates change
    useEffect(() => {
        const fetchRoute = async () => {
            setLoading(true);
            try {
                const response = await axios.get(
                    `https://api.mapbox.com/directions/v5/mapbox/driving/${ECLIPSE_TINT_COORDS.lng},${ECLIPSE_TINT_COORDS.lat};${siteLng},${siteLat}`,
                    {
                        params: {
                            geometries: "geojson",
                            overview: "full",
                            access_token: MAPBOX_TOKEN,
                        },
                    }
                );

                const data = response.data;
                console.log("🚩 Mapbox response:", data);

                if (data.routes && data.routes.length > 0) {
                    const route = data.routes[0];
                    const coords = route.geometry.coordinates;

                    // Create GeoJSON for the route
                    const routeData = {
                        type: 'Feature',
                        properties: {},
                        geometry: {
                            type: 'LineString',
                            coordinates: coords
                        }
                    };

                    setRouteGeoJson({
                        type: 'FeatureCollection',
                        features: [routeData]
                    });

                    const calculatedDistance = (route.distance / 1000).toFixed(2);
                    const calculatedDuration = (route.duration / 60).toFixed(0);
                    setDistance(calculatedDistance);
                    setDuration(calculatedDuration);

                    console.log("✅ Distance:", calculatedDistance, "km");
                    console.log("✅ Duration:", calculatedDuration, "mins");

                    // Auto center and zoom to fit the entire route
                    const lats = coords.map((c) => c[1]);
                    const lngs = coords.map((c) => c[0]);

                    // Calculate bounding box
                    const minLat = Math.min(...lats);
                    const maxLat = Math.max(...lats);
                    const minLng = Math.min(...lngs);
                    const maxLng = Math.max(...lngs);

                    // Add padding to the bounds
                    const latPadding = (maxLat - minLat) * 0.3;
                    const lngPadding = (maxLng - minLng) * 0.3;

                    // Calculate appropriate zoom level to show the entire route
                    // Lower zoom number = more zoomed out
                    const fitBoundsZoom = calculateZoomLevel(
                        [minLng - lngPadding, minLat - latPadding],
                        [maxLng + lngPadding, maxLat + latPadding],
                        { width: 400, height: 350 } // Approximate container size
                    );

                    setViewState({
                        latitude: (minLat + maxLat) / 2,
                        longitude: (minLng + maxLng) / 2,
                        zoom: fitBoundsZoom,
                        bearing: 0,
                        pitch: 0,
                    });
                } else {
                    console.warn("⚠️ No route data found in response:", data);
                    setDistance(null);
                    setDuration(null);
                    setRouteGeoJson(null);
                }
            } catch (error) {
                console.error("❌ Failed to fetch route:", error);
                setDistance(null);
                setDuration(null);
                setRouteGeoJson(null);
            } finally {
                setLoading(false);
            }
        };

        fetchRoute();
    }, [siteLat, siteLng]);

    // Helper function to calculate appropriate zoom level
    const calculateZoomLevel = (sw, ne, mapDimensions) => {
        const latFraction = (ne[1] - sw[1]) / 180;
        const lngFraction = (ne[0] - sw[0]) / 360;

        const latZoom = Math.log2(mapDimensions.height / 256 / latFraction);
        const lngZoom = Math.log2(mapDimensions.width / 256 / lngFraction);

        // Use the smaller zoom level to ensure the entire route is visible
        return Math.min(latZoom, lngZoom, 16) - 0.5; // Subtract a bit to add padding
    };

    // Route info overlay
    const RouteInfoOverlay = useMemo(() => {
        if (!showRouteInfo) return null;

        if (loading) {
            return (
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: 20,
                        left: 20,
                        zIndex: 1,
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: 'rgba(0, 0, 0, 0.4)',
                        color: '#fff',
                        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.2)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        width: 'auto',
                        maxWidth: '300px',
                        textAlign: 'left'
                    }}
                >
                    <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        ⏳ Loading route...
                    </Typography>
                </Box>
            );
        }

        if (distance && duration) {
            return (
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: 20,
                        left: 20,
                        zIndex: 1,
                        p: 2,
                        borderRadius: 2,
                        bgcolor: 'rgba(0, 0, 0, 0.3)',
                        color: '#fff',
                        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.2)',
                        backdropFilter: 'blur(3px)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        width: 'auto',
                        maxWidth: '320px',
                        minWidth: '280px',
                        textAlign: 'left'
                    }}
                >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Typography variant="body2" sx={{
                            fontWeight: 'bold',
                            color: '#4fc3f7',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                        }}>
                            🚗 {distance} km • {duration} min drive
                        </Typography>
                        <IconButton
                            size="small"
                            onClick={() => setShowRouteInfo(false)}
                            sx={{
                                color: '#fff',
                                p: 0.5,
                                ml: 1,
                                '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' }
                            }}
                        >
                            <X size={16} />
                        </IconButton>
                    </Box>

                    <Typography variant="caption" sx={{
                        color: '#b3e5fc',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 0.5
                    }}>
                        🏢 From: Eclipse Tint Office
                    </Typography>

                    <Typography variant="caption" sx={{
                        color: '#b3e5fc',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 1
                    }}>
                        📍 To: {siteName}
                    </Typography>

                    <Typography variant="caption" sx={{
                        color: '#81c784',
                        fontWeight: 'medium',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                    }}>
                        ⏰ Allow {parseInt(duration) + 10} mins for travel
                    </Typography>
                </Box>
            );
        }

        if (!loading && (!distance || !duration)) {
            return (
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: 20,
                        left: 20,
                        zIndex: 1,
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: 'rgba(211, 47, 47, 0.4)',
                        color: '#fff',
                        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.2)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        width: 'auto',
                        maxWidth: '300px',
                        textAlign: 'left'
                    }}
                >
                    <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        ⚠️ No route found
                    </Typography>
                </Box>
            );
        }

        return null;
    }, [loading, distance, duration, showRouteInfo, siteName]);

    // Floating toggle button
    const FloatingToggleButton = useMemo(() => {
        return (
            <IconButton
                onClick={() => setShowRouteInfo(!showRouteInfo)}
                sx={{
                    position: 'absolute',
                    top: 20,
                    right: 20,
                    zIndex: 2,
                    bgcolor: 'rgba(0, 0, 0, 0.6)',
                    color: '#fff',
                    width: 48,
                    height: 48,
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    '&:hover': {
                        bgcolor: 'rgba(0, 0, 0, 0.8)',
                        transform: 'scale(1.05)'
                    },
                    transition: 'all 0.2s ease-in-out'
                }}
            >
                <Info size={24} />
            </IconButton>
        );
    }, [showRouteInfo]);

    return (
        <div
            style={{
                height: 350,
                width: "100%",
                borderRadius: "8px",
                overflow: "hidden",
                marginTop: "16px",
                position: "relative",
            }}
        >
            <Map
                mapboxAccessToken={MAPBOX_TOKEN}
                mapStyle="mapbox://styles/mapbox/dark-v11"
                {...viewState}
                onMove={evt => setViewState(evt.viewState)}
                attributionControl={false}
            >
                <NavigationControl position="top-left" />

                {/* Route line */}
                {routeGeoJson && (
                    <Source id="route-source" type="geojson" data={routeGeoJson}>
                        <Layer {...routeLineLayer} />
                    </Source>
                )}

                {/* Start marker (Eclipse Tint) */}
                <Marker
                    longitude={ECLIPSE_TINT_COORDS.lng}
                    latitude={ECLIPSE_TINT_COORDS.lat}
                    anchor="bottom"
                    onClick={e => {
                        e.originalEvent.stopPropagation();
                        handleMarkerClick({
                            longitude: ECLIPSE_TINT_COORDS.lng,
                            latitude: ECLIPSE_TINT_COORDS.lat
                        }, 'start');
                    }}
                >
                    <div style={{
                        width: 40,
                        height: 40,
                        backgroundImage: 'url(https://cdn-icons-png.flaticon.com/512/9356/9356230.png)',
                        backgroundSize: 'cover',
                        filter: 'drop-shadow(0px 2px 5px rgba(0,0,0,0.5))',
                        cursor: 'pointer'
                    }} />
                </Marker>

                {/* End marker (Client location) */}
                <Marker
                    longitude={siteLng}
                    latitude={siteLat}
                    anchor="bottom"
                    onClick={e => {
                        e.originalEvent.stopPropagation();
                        handleMarkerClick({
                            longitude: siteLng,
                            latitude: siteLat
                        }, 'end');
                    }}
                >
                    <div style={{
                        width: 40,
                        height: 40,
                        backgroundImage: 'url(https://cdn-icons-png.flaticon.com/512/2776/2776067.png)',
                        backgroundSize: 'cover',
                        filter: 'drop-shadow(0px 2px 5px rgba(0,0,0,0.5))',
                        cursor: 'pointer'
                    }} />
                </Marker>

                {/* Popup */}
                {showPopup && popupInfo && (
                    <Popup
                        longitude={popupInfo.longitude}
                        latitude={popupInfo.latitude}
                        anchor="bottom"
                        onClose={() => setShowPopup(false)}
                        closeOnClick={false}
                        closeButton={true}
                        style={{
                            zIndex: 3
                        }}
                    >
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                            {popupInfo.title}
                        </Typography>
                        <Typography variant="caption">
                            {popupInfo.type === 'start' ? 'Starting point' : 'Destination'}
                        </Typography>
                    </Popup>
                )}
            </Map>

            {/* Floating toggle button */}
            {FloatingToggleButton}

            {/* Route info overlay */}
            {RouteInfoOverlay}
        </div>
    );
};

export default SiteMap;
